import type { CSSProperties, ReactNode } from "react";
import { cn } from "@utils/cn.ts";
import type {
  RosterTableLayout,
  RosterTableLayoutConfig,
} from "@/components/nurse/rosterColumnHoverLayout";
import {
  ROSTER_BODY_FIRST_COL_PX,
  ROSTER_COMPACT_BODY_FIRST_COL_PX,
  ROSTER_COMPACT_SUMMARY_FIRST_COL_PX,
  ROSTER_PREV_TAIL_HEADER_PX,
  ROSTER_SUMMARY_FIRST_COL_PX,
} from "@/components/nurse/rosterColumnHoverLayout";
import { RosterGridType } from "./roster-grid-type";

type RosterGridProps = {
  className?: string;
  children: ReactNode;
  hasPrevTail?: boolean;
  gridType?: RosterGridType;
  layout?: RosterTableLayout;
  layoutConfig?: RosterTableLayoutConfig;
};

type RosterGridStyle = CSSProperties & {
  readonly "--roster-grid-first-col-width"?: string;
};

export default function RosterGrid({
  className,
  children,
  gridType,
  hasPrevTail,
  layout = "create",
  layoutConfig,
}: RosterGridProps) {
  const firstColPx =
    gridType === RosterGridType.BODY
      ? layoutConfig?.bodyFirstColPx
      : hasPrevTail
        ? layoutConfig?.summaryFirstColWithPrevTailPx
        : layoutConfig?.summaryFirstColPx;
  const fallbackFirstColPx =
    gridType === RosterGridType.BODY
      ? layout === "compact"
        ? ROSTER_COMPACT_BODY_FIRST_COL_PX
        : ROSTER_BODY_FIRST_COL_PX
      : hasPrevTail
        ? layout === "compact"
          ? ROSTER_COMPACT_SUMMARY_FIRST_COL_PX + ROSTER_PREV_TAIL_HEADER_PX
          : ROSTER_SUMMARY_FIRST_COL_PX + ROSTER_PREV_TAIL_HEADER_PX
        : layout === "compact"
          ? ROSTER_COMPACT_SUMMARY_FIRST_COL_PX
          : ROSTER_SUMMARY_FIRST_COL_PX;
  const resolvedFirstColPx = firstColPx ?? fallbackFirstColPx;
  const style: RosterGridStyle | undefined =
    resolvedFirstColPx != null
      ? { "--roster-grid-first-col-width": `${resolvedFirstColPx}px` }
      : undefined;

  return (
    <div
      style={style}
      className={cn(
        "flex [&>*:first-child]:flex-shrink-0 gap-3.5",
        "[&>*:first-child]:w-[var(--roster-grid-first-col-width)]",
        className
      )}
    >
      {children}
    </div>
  );
}
